import { useEffect } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Sun,
    Zap,
    ShieldCheck,
    DollarSign,
    Lightbulb,
    HelpCircle,
    Ruler,
    Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

// Real Solatube / solar tube installation photos sourced from Wikimedia Commons
// (Creative Commons — credited in the page footer attribution line).
const IMG_HERO_DIFFUSER =
    "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg/1280px-Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg";
const IMG_ROOF_DOMES =
    "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Solar_Tubes_%285202618265%29.jpg/1280px-Solar_Tubes_%285202618265%29.jpg";
const IMG_LIVING_ROOM =
    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/Un_conduit_de_lumi%C3%A8re_naturelle_dans_un_salon_-_Solatube.jpg/1024px-Un_conduit_de_lumi%C3%A8re_naturelle_dans_un_salon_-_Solatube.jpg";

const BENEFITS = [
    { i: Sun, t: "Real daylight, indoors", d: "Bright, natural sunlight in any room — even ones with no walls for windows. That's what a solartube does." },
    { i: Zap, t: "Cut your light bill", d: "Most rooms don't need a single bulb during daylight hours after a solartube goes in." },
    { i: ShieldCheck, t: "No heat gain · no UV", d: "Unlike a standard skylight, a solartube filters out heat and UV so your floors and furniture don't fade." },
    { i: DollarSign, t: "Fast payback", d: "A fraction of the cost of a traditional skylight — installs in a day, lasts decades." },
];

const SIZES = [
    { size: "6\"", rooms: "Small closets, pantries, half-baths" },
    { size: "10\"", rooms: "Bathrooms, hallways, laundry rooms" },
    { size: "14\"", rooms: "Bedrooms, kitchens, home offices" },
    { size: "18\"", rooms: "Living rooms, dining rooms, open spaces" },
    { size: "22\"", rooms: "Great rooms, large open-plan areas, commercial" },
];

const ROOMS = [
    "Bathrooms (interior)",
    "Hallways with no windows",
    "Walk-in closets",
    "Laundry rooms",
    "Kitchens & pantries",
    "Home offices",
    "Bedrooms",
    "Garages & workshops",
    "Stairwells",
    "Commercial spaces",
];

const PROCESS = [
    { i: HelpCircle, t: "Free site visit", d: "We check your roof, attic, and ceiling to pick the right spot and size for your solartube." },
    { i: Ruler, t: "Measure & spec", d: "Confirm 6\"–22\" diameter, run length, and your room's daylight target." },
    { i: Wrench, t: "Install in a day", d: "Roof flashing, dome, reflective tube, ceiling diffuser — sealed and dust-controlled." },
    { i: Lightbulb, t: "Enjoy the sun", d: "We turn the lights off and show you the difference before we leave." },
];

export default function SolarTubes() {
    useSeo({
        title: "Solartube Skylight Installation · Sun Tunnels · Medford, Oregon | 5 Star Construction",
        description: "Solartube skylight installation in Medford, Ashland, and the Rogue Valley — also known as sun tunnels or tubular skylights. 6\" to 22\" diameters, installed in a day. Pipe real daylight into any windowless room. Licensed Oregon contractor (CCB #232477). Free quotes — 541-292-9620.",
        path: "/solar-tubes",
        jsonLdKey: "solar-tubes",
        jsonLd: [
            serviceSchema({
                name: "Solartube Skylight Installation",
                description: "Installation of solartube skylights (also called sun tunnels or tubular skylights), 6\"–22\" diameter, for homes in Medford and Southern Oregon. Brings natural daylight into bathrooms, hallways, closets, and any windowless room.",
                path: "/solar-tubes",
                serviceType: "Solar Tube Installation",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Solartubes", path: "/solar-tubes" },
            ]),
        ],
    });
    useEffect(() => {}, []);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Sun className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Solartube Installers · Medford, OR</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Southern Oregon&apos;s
                            <br />
                            <span className="text-[#FFD400]">solartube</span> crew.
                        </h1>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-2xl">
                            We install <strong className="text-[#F6F5F2]">solar tubes</strong> — also commonly called <em>sun tunnels</em> or <em>tubular skylights</em> — that pipe bright, natural daylight from your roof to any windowless room. 6&quot; to 22&quot; diameter. Installed clean, in a day.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="st-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="st-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    REQUEST A FREE SOLARTUBE QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] w-full overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block">
                            <img
                                src={IMG_HERO_DIFFUSER}
                                alt="Solartube ceiling diffuser lighting a kitchen with pure natural daylight — no bulbs on"
                                loading="eager"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 inset-x-0 bg-[#FFD400] text-[#111111] p-4">
                                <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.22em] font-bold">Solartube diffuser · interior</div>
                                <div className="font-display text-lg font-extrabold mt-0.5">Real daylight · No bulb required</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why solar tubes */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why a solartube</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                Brighter rooms.
                                <br />
                                Lower bills.
                                <br />
                                No remodel.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                A solartube pipes pure daylight from a small dome on your roof, through a highly-reflective tube in your attic, and out a diffuser in your ceiling. The result: a windowless bathroom that&apos;s bright as the outdoors, a hallway that doesn&apos;t need a single bulb during the day, and a power bill that quietly shrinks. No structural changes, no permits in most cases, no surprises — that&apos;s what makes solar tubes (a.k.a. sun tunnels or tubular skylights) the smartest daylighting upgrade we install.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {BENEFITS.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6">
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works - roof shot */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 relative">
                        <div className="aspect-[4/3] overflow-hidden border-2 border-[#111111] shadow-block">
                            <img
                                src={IMG_ROOF_DOMES}
                                alt="Multiple solartube roof domes installed on a residential roof — the daylight intake side"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -top-4 -left-4 hidden md:block bg-[#FFD400] text-[#111111] p-4 border-2 border-[#111111] shadow-block-sm">
                            <div className="font-['Anton'] text-3xl tracking-tight leading-none">6&quot;–22&quot;</div>
                            <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.18em] font-bold mt-1">Solartube diameter</div>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">How a solartube goes in</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            From roof to ceiling — one day.
                        </h2>
                        <ol className="mt-8 space-y-5">
                            {PROCESS.map(({ i: I, t, d }, idx) => (
                                <li key={t} className="flex gap-5 items-start" data-testid={`st-step-${idx}`}>
                                    <div className="shrink-0 w-12 h-12 bg-[#111111] text-[#FFD400] flex items-center justify-center font-['Anton'] text-xl">
                                        0{idx + 1}
                                    </div>
                                    <div>
                                        <div className="font-display text-lg md:text-xl font-extrabold text-[#111812] flex items-center gap-2">
                                            <I className="w-5 h-5 text-[#E11D2A]" strokeWidth={2.25} />
                                            {t}
                                        </div>
                                        <p className="font-['Manrope'] text-base text-[#5F5F5F] mt-1 leading-relaxed">{d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* Sizes */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-12">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Solartube sizes & coverage</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl md:text-6xl">
                            From a 6&quot; pantry punch to a 22&quot; great-room flood.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] max-w-3xl mx-auto">
                            Pick a solartube size by the room you&apos;re lighting. Not sure? We&apos;ll measure and recommend during the free site visit.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2DFD8] border border-[#E2DFD8]">
                        {SIZES.map((s) => (
                            <div key={s.size} className="bg-white p-6 hover:bg-[#F6F5F2] transition-colors" data-testid={`st-size-${s.size.replace(/\D/g, "")}`}>
                                <div className="font-['Anton'] text-5xl text-[#E11D2A] tracking-tight">{s.size}</div>
                                <div className="font-['Manrope'] text-xs uppercase tracking-[0.2em] font-bold mt-2 text-[#5F5F5F]">Diameter</div>
                                <p className="mt-4 pt-4 border-t border-[#E2DFD8] font-['Manrope'] text-sm text-[#111812] leading-snug">{s.rooms}</p>
                            </div>
                        ))}
                    </div>

                    {/* Rooms grid */}
                    <div className="mt-16">
                        <div className="font-['Manrope'] text-xs uppercase tracking-[0.22em] font-black text-[#111111] mb-5">Most-requested solartube rooms</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {ROOMS.map((r) => (
                                <div key={r} className="bg-[#F6F5F2] border border-[#E2DFD8] px-4 py-3 flex items-center gap-2 text-sm font-semibold text-[#111812]">
                                    <Check className="w-4 h-4 text-[#E11D2A] shrink-0" strokeWidth={3} />
                                    {r}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Living-room install showcase */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">See it installed</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            A solartube looks like a flush ceiling light — but the power switch is the sun.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed max-w-2xl">
                            The diffuser sits flat in your ceiling and throws a soft, even daylight glow across the whole room. No glare, no hotspot, no shadows. Once it&apos;s in, most folks forget it isn&apos;t a light fixture — until they notice they haven&apos;t flipped a switch in months.
                        </p>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="aspect-[4/5] overflow-hidden border-2 border-[#111111] shadow-block">
                            <img
                                src={IMG_LIVING_ROOM}
                                alt="Living room lit by a Solatube solartube ceiling diffuser — even, glare-free daylight"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Leading area contractor callout */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 mb-5 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Sun className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Southern Oregon&apos;s leading solartube installer</span>
                        </div>
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.92] text-4xl sm:text-5xl lg:text-6xl">
                            The crew that&apos;s done <span className="text-[#FFD400]">the most</span> solartubes in the Rogue Valley.
                        </h2>
                        <p className="mt-6 font-['Manrope'] text-lg md:text-xl text-[#F6F5F2]/80 max-w-2xl leading-relaxed">
                            Licensed Oregon General Contractor (CCB #232477) with 25 years on the tools. Every solartube install includes proper roof flashing, full attic sealing, ceiling-diffuser finish work, and a one-day turnaround on most homes.
                        </p>
                    </div>
                    <ul className="lg:col-span-5 grid grid-cols-1 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {[
                            { t: "Watertight roof flashing", d: "Sealed properly the first time — no callbacks." },
                            { t: "Full attic dust containment", d: "Plastic and protection top to bottom. We respect your house." },
                            { t: "Drywall & paint finish", d: "Ceiling diffuser blended, patched, and ready to use." },
                            { t: "One-day install on most jobs", d: "Out by dinner. Lights off by sunset." },
                        ].map((b) => (
                            <li key={b.t} className="bg-[#111111] p-6">
                                <div className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-[#FFD400] mt-1 shrink-0" strokeWidth={3} />
                                    <div>
                                        <div className="font-display text-lg font-extrabold text-[#F6F5F2]">{b.t}</div>
                                        <div className="font-['Manrope'] text-sm text-[#F6F5F2]/70 mt-1">{b.d}</div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Got a dark room that needs a solartube?
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free quotes. Honest pricing. Real Oregon contractor with 25 years on the tools. Call or send a request — we&apos;ll get back to you the same day.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="st-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="st-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Image attribution (Wikimedia Commons photos used as examples) */}
            <section className="bg-[#111111] text-[#F6F5F2]/55 border-t border-[#F6F5F2]/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    Example solartube interior &amp; roof-dome photos via Wikimedia Commons (Solatube product photos, CC BY-SA). Used for illustration; not photos of jobs we&apos;ve personally completed.
                </div>
            </section>

            <Footer />
        </div>
    );
}
